import type { ReactNode } from 'react';
import { Icono } from './Icono';

/**
 * Tabla o lista (componentes.md §12 y §12b): tarjeta blanca radio 12 con `--rv-shadow-2` y sin borde; cabecera en
 * eyebrow 11/700 `--rv-fg-3`; filas de 64 como mínimo con separador 1 `--rv-rule` y chevron al final si la fila abre su
 * ficha; acciones de fila como iconos fantasma antes del chevron. En móvil (y iPad vertical) cada fila es una tarjeta con
 * lo que dé `tarjeta(fila)` y el chevron. La paginación (RV Paginación) va debajo, fuera de la tabla.
 */
export interface Columna {
    clave: string;
    titulo: string;
    /** Ancho en la rejilla (`minmax(0, 2fr)`, `120px`…). Por defecto, `minmax(0, 1fr)`. */
    ancho?: string;
    alinear?: 'izquierda' | 'derecha';
}

export interface FilaTabla {
    clave: string;
    celdas: Readonly<Record<string, ReactNode>>;
    /** Lo que se ve en móvil (nombre, línea secundaria…). Sin ello, las celdas una debajo de otra. */
    tarjeta?: ReactNode;
    acciones?: ReactNode;
    onAbrir?: () => void;
    /** Nombre de la fila para el lector de pantalla («Abrir la ficha de …»). */
    etiqueta?: string;
    /** La fila que pide el enlace (p. ej. `?exportacion=` desde la campana, G888): fondo y filete rojo, `aria-current`. */
    resaltada?: boolean;
}

export function Tabla({ columnas, filas, movil = false, etiqueta, vacia }: { columnas: readonly Columna[]; filas: readonly FilaTabla[]; movil?: boolean; etiqueta: string; vacia?: ReactNode }) {
    const conAcciones = filas.some((f) => f.acciones !== undefined);
    const conChevron = filas.some((f) => f.onAbrir !== undefined);
    const plantilla = [...columnas.map((c) => c.ancho ?? 'minmax(0, 1fr)'), ...(conAcciones ? ['auto'] : []), ...(conChevron ? ['24px'] : [])].join(' ');

    if (filas.length === 0 && vacia !== undefined) {
        return <div className="rv-tabla rv-tabla--vacia">{vacia}</div>;
    }

    if (movil) {
        return (
            <div role="list" aria-label={etiqueta} className="rv-tarjetas">
                {filas.map((f) => {
                    const dentro = (
                        <>
                            <span className="rv-tarjetas__texto">
                                {f.tarjeta ??
                                    columnas.map((c) => (
                                        <span key={c.clave} className="rv-tarjetas__dato">
                                            {f.celdas[c.clave]}
                                        </span>
                                    ))}
                            </span>
                            {f.acciones !== undefined && (
                                <span className="rv-tabla__acciones" onClick={(e) => { e.stopPropagation(); }}>
                                    {f.acciones}
                                </span>
                            )}
                            {f.onAbrir !== undefined && <Icono nombre="chevron-right" />}
                        </>
                    );
                    return f.onAbrir === undefined ? (
                        <div key={f.clave} role="listitem" className={`rv-tarjetas__fila${f.resaltada === true ? ' rv-tarjetas__fila--resaltada' : ''}`} aria-current={f.resaltada === true ? 'true' : undefined}>
                            {dentro}
                        </div>
                    ) : (
                        <div key={f.clave} role="listitem" className={`rv-tarjetas__fila rv-tarjetas__fila--abre${f.resaltada === true ? ' rv-tarjetas__fila--resaltada' : ''}`} aria-current={f.resaltada === true ? 'true' : undefined} tabIndex={0} aria-label={f.etiqueta} onClick={f.onAbrir} onKeyDown={(e) => { if (e.key === 'Enter') { f.onAbrir?.(); } }}>
                            {dentro}
                        </div>
                    );
                })}
            </div>
        );
    }

    return (
        <div role="table" aria-label={etiqueta} className="rv-tabla">
            <div role="row" className="rv-tabla__cab" style={{ gridTemplateColumns: plantilla }}>
                {columnas.map((c) => (
                    <span key={c.clave} role="columnheader" style={{ textAlign: c.alinear === 'derecha' ? 'right' : 'left' }}>
                        {c.titulo}
                    </span>
                ))}
                {conAcciones && <span role="columnheader" aria-label="Acciones" />}
                {conChevron && <span aria-hidden="true" />}
            </div>
            {filas.map((f) => (
                <div
                    key={f.clave}
                    role="row"
                    className={`rv-tabla__fila${f.onAbrir === undefined ? '' : ' rv-tabla__fila--abre'}${f.resaltada === true ? ' rv-tabla__fila--resaltada' : ''}`}
                    aria-current={f.resaltada === true ? 'true' : undefined}
                    style={{ gridTemplateColumns: plantilla }}
                    tabIndex={f.onAbrir === undefined ? undefined : 0}
                    aria-label={f.etiqueta}
                    onClick={f.onAbrir}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            f.onAbrir?.();
                        }
                    }}
                >
                    {columnas.map((c) => (
                        <span key={c.clave} role="cell" style={{ textAlign: c.alinear === 'derecha' ? 'right' : 'left' }}>
                            {f.celdas[c.clave]}
                        </span>
                    ))}
                    {conAcciones && (
                        <span role="cell" aria-label="Acciones" className="rv-tabla__acciones" onClick={(e) => { e.stopPropagation(); }}>
                            {f.acciones}
                        </span>
                    )}
                    {conChevron && <span aria-hidden="true">{f.onAbrir !== undefined && <Icono nombre="chevron-right" />}</span>}
                </div>
            ))}
        </div>
    );
}
